/**
 * Utility functions for smooth scrolling to page sections
 */

/**
 * Smoothly scrolls to an element by ID or selector
 * @param {string} selector - CSS selector or element ID (with or without #)
 * @param {object} options - Scroll options
 * @param {number} options.offset - Offset in pixels from the top (default: 0)
 * @param {string} options.behavior - Scroll behavior: 'smooth' | 'auto' (default: 'smooth')
 * @param {number} options.timeout - Delay before scrolling in ms (default: 0)
 * @returns {boolean} True if element was found and scrolled to, false otherwise
 */
export const scrollToSection = (selector, options = {}) => {
  const { offset = 0, behavior = 'smooth', timeout = 0 } = options;

  // Normalize selector (remove # if present, add it back for querySelector)
  const normalizedSelector = selector.startsWith('#') ? selector : `#${selector}`;

  const scroll = () => {
    const element = document.querySelector(normalizedSelector);

    if (!element) {
      console.warn(`Element not found: ${normalizedSelector}`);
      return false;
    }

    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior,
    });

    return true;
  };

  if (timeout > 0) {
    setTimeout(scroll, timeout);
    return true;
  }

  return scroll();
};

/**
 * Scrolls to FAQ section (commonly used from navigation)
 * @param {object} options - Scroll options
 * @returns {boolean} True if FAQ section was found and scrolled to
 */
export const scrollToFaq = (options = {}) => {
  return scrollToSection('faq', options);
};

/**
 * Scrolls to contact section
 * @param {object} options - Scroll options
 * @returns {boolean} True if contact section was found and scrolled to
 */
export const scrollToContact = (options = {}) => {
  return scrollToSection('contact', options);
};

/**
 * Scrolls to top of the page
 * @param {object} options - Scroll options
 */
export const scrollToTop = (options = {}) => {
  const { behavior = 'smooth', timeout = 0 } = options;

  const scroll = () => {
    window.scrollTo({
      top: 0,
      behavior,
    });
  };

  if (timeout > 0) {
    setTimeout(scroll, timeout);
  } else {
    scroll();
  }
};

export default {
  scrollToSection,
  scrollToFaq,
  scrollToContact,
  scrollToTop,
};
